export interface AdminUser {
  _id: string
  username: string
  email: string
}

export interface AccountRef {
  _id: string
  first_name: string
  last_name: string
  email: string
  phone?: string
  company_name?: string
}

export type MeetingType = 'onboarding_new' | 'onboarding' | 'followup' | 'technical' | 'sales' | 'other'
export type MeetingStatus = 'scheduled' | 'completed' | 'cancelled' | 'no_show'

export interface InternalNote {
  _id: string
  admin: AdminUser | string
  text: string
  type: 'manual' | 'system'
  created_at: string
}

export interface WhatsAppMessage {
  _id: string
  type: 'scheduled' | 'reminder'
  sent_at: string
  sent_by?: AdminUser | string
  phone_number?: string
  message_text?: string
  status: 'sent' | 'failed'
  error?: string
}

export interface WhatsAppTemplates {
  scheduled: string
  reminder: string
}

export interface AdminMeeting {
  _id: string
  // null for a meeting an affiliate booked for a lead that has no account yet;
  // contact_name / contact_phone carry who it's with in that case.
  account: AccountRef | string | null
  lead?: string | null
  contact_name?: string
  contact_phone?: string
  assigned_to: AdminUser | string
  claimed_by?: AdminUser | string | null
  created_by: AdminUser | string | null
  // Set when an affiliate booked the meeting for their referred customer from
  // the affiliate portal (source === 'affiliate'); created_by is null then.
  created_by_affiliate?: {_id: string; first_name?: string; last_name?: string; company_name?: string; phone?: string} | string | null
  source?: 'admin' | 'affiliate'
  meeting_date: string
  meeting_type: MeetingType
  status: MeetingStatus
  title: string
  notes?: string
  summary?: string
  completed_at?: string
  whatsapp_messages?: WhatsAppMessage[]
  internal_notes?: InternalNote[]
  created_at: string
  updated_at: string
}

export interface ProfileStatus {
  _id: string
  first_name: string
  last_name: string
  facebook_username: string
  facebook_id: string
  profile_picture_url: string
  is_logged_in: boolean
}

export interface CampaignStatus {
  _id: string
  name: string
  post_count: number
  group_count: number
}

export interface SubProfileStatus extends ProfileStatus {
  parent_profile: string
}

export interface OnboardingNotes {
  profiles: string
  campaigns: string
  groups: string
  posts: string
}

export interface AccountOnboardingStatus {
  profiles: ProfileStatus[]
  subProfiles: SubProfileStatus[]
  campaigns: CampaignStatus[]
  onboarding_notes: OnboardingNotes
  total_groups: number
  total_posts: number
}

export interface MeetingStats {
  todayRemaining: number
  within30min: number
  within1hour: number
  bubbleColor: 'green' | 'yellow' | 'red'
  needsFlash: boolean
  needsHeaderFlash: boolean
  unclaimedUrgent: Array<{_id: string; meeting_date: string}>
}

export interface MeetingsQueryResponse {
  meetings: AdminMeeting[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

export const MEETING_TYPE_LABELS: Record<MeetingType, string> = {
  onboarding_new: 'אונבורדינג לקוח חדש',
  onboarding: 'אונבורדינג',
  followup: 'מעקב',
  technical: 'טכני',
  sales: 'מכירות',
  other: 'אחר'
}

export const MEETING_STATUS_LABELS: Record<MeetingStatus, string> = {
  scheduled: 'מתוזמן',
  completed: 'הושלם',
  cancelled: 'בוטל',
  no_show: 'לא הגיע'
}

export const MEETING_TYPE_COLORS: Record<MeetingType, string> = {
  onboarding_new: 'success',
  onboarding: 'primary',
  followup: 'info',
  technical: 'warning',
  sales: 'success',
  other: 'secondary'
}

export const MEETING_STATUS_COLORS: Record<MeetingStatus, string> = {
  scheduled: 'primary',
  completed: 'success',
  cancelled: 'secondary',
  no_show: 'danger'
}
